import React, { useCallback, useEffect, useState } from 'react';
import axios from '@mysimpledb/shared/config/axios';
import MyNotesWeb from './MyNotesWeb';
import ListNotesWeb from './ListNotesWeb';
import './MyNotesTabsWeb.css';
import { fetchEventSource } from '@microsoft/fetch-event-source';

const NOTE_TITLE_MAX_LENGTH = 100;

const INITIAL_FORM = {
  id: null,
  title: '',
  titleHtml: '',
  detail: '',
  detailHtml: '',
  dueAt: '',
  remindAt: '',
  expiresAt: '',
  sendSmsReminder: false,
  displayMode: 'normal',
  categoryIds: [],
  categories: [],
  primaryCategoryId: null,
  updatedAt: null,
  userId: null,
};

export default function MyNotesTabWeb({
  userId,
  searchTerm,
  setSearchTerm,
  setSearchType,
}) {
  const [navSource, setNavSource] = useState('tab');
  const [notes, setNotes] = useState([]);
  const [totalNotesFound, setTotalNotesFound] = useState(0);
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [notePageNo, setNotePageNo] = useState(0);
  const [listPageNo, setListPageNo] = useState(0);
  const [listPageClicked, setListPageClicked] = useState(0);
  const [activeTab, setActiveTab] = useState('list');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [categoryMatchMode, setCategoryMatchMode] = useState('any');
  const [uncategorizedOnly, setUncategorizedOnly] = useState(false);
  const [customViews, setCustomViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);

  const token = localStorage.getItem('token');
  const itemsPerPage = 30;

  const [form, setForm] = useState({
    ...INITIAL_FORM,
    userId: userId || null,
  });

  const mapNoteFromApi = useCallback(
    (note) => ({
      id: note.id,
      title: note.title || '',
      titleHtml: note.title_html || '',
      detail: note.detail || '',
      detailHtml: note.detail_html || '',
      dueAt: note.due_at || '',
      remindAt: note.remind_at || '',
      expiresAt: note.expires_at || '',
      sendSmsReminder: !!note.send_sms_reminder,
      displayMode: note.display_mode || 'normal',
      categories: Array.isArray(note.categories) ? note.categories : [],
      categoryIds: Array.isArray(note.categories)
        ? note.categories.map((category) => Number(category.id))
        : [],
      primaryCategoryId:
        note.primary_category_id != null
          ? Number(note.primary_category_id)
          : note.primaryCategoryId != null
            ? Number(note.primaryCategoryId)
            : Array.isArray(note.categories) && note.categories.length
              ? Number(note.categories[0].id)
              : null,
      updatedAt: note.updated_at || null,
      userId: note.user_id ?? userId ?? null,
    }),
    [userId]
  );

  const fetchCategories = useCallback(
    async (authToken = token) => {
      if (!authToken) return [];

      const res = await axios.get('/notes/categories', {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const nextCategories = Array.isArray(res.data?.categories)
        ? res.data.categories
        : [];

      setCategories(nextCategories);
      return nextCategories;
    },
    [token]
  );


  const fetchViews = useCallback(
    async (authToken = token) => {
      if (!authToken) return [];

      const res = await axios.get('/notes/views', {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const nextViews = Array.isArray(res.data?.data) ? res.data.data : [];
      setCustomViews(nextViews);

      setActiveViewId((currentId) => {
        if (
          currentId != null &&
          !nextViews.some((view) => String(view.id) === String(currentId))
        ) {
          return null;
        }

        return currentId;
      });

      return nextViews;
    },
    [token]
  );

  const createCustomView = useCallback(
    async (view, authToken = token) => {
      const res = await axios.post(
        '/notes/views',
        {
          name: String(view?.name || '').trim(),
          viewType: 'filtered',
          categoryIds: Array.isArray(view?.categoryIds)
            ? view.categoryIds.map(Number)
            : [],
          displayMode:
            view?.displayMode === 'normal' || view?.displayMode === 'expanded'
              ? view.displayMode
              : 'all',
          hasReminder: !!view?.hasReminder,
          reminderPastDue: !!view?.reminderPastDue,
          hasDueDate: !!view?.hasDueDate,
          dueDatePastDue: !!view?.dueDatePastDue,
          noteIds: [],
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      await fetchViews(authToken);
      return res.data?.view || null;
    },
    [fetchViews, token]
  );


  const updateCustomView = useCallback(
    async (viewId, view, authToken = token) => {
      const res = await axios.put(
        `/notes/views/${viewId}`,
        {
          name: String(view?.name || '').trim(),
          viewType: view?.viewType === 'freeform' ? 'freeform' : 'filtered',
          categoryIds:
            view?.viewType === 'freeform'
              ? []
              : Array.isArray(view?.categoryIds)
                ? view.categoryIds.map(Number)
                : [],
          displayMode:
            view?.viewType === 'freeform'
              ? 'all'
              : view?.displayMode === 'normal' ||
                  view?.displayMode === 'expanded'
                ? view.displayMode
                : 'all',
          hasReminder:
            view?.viewType === 'freeform' ? false : !!view?.hasReminder,
          reminderPastDue:
            view?.viewType === 'freeform' ? false : !!view?.reminderPastDue,
          hasDueDate:
            view?.viewType === 'freeform' ? false : !!view?.hasDueDate,
          dueDatePastDue:
            view?.viewType === 'freeform' ? false : !!view?.dueDatePastDue,
          noteIds:
            view?.viewType === 'freeform' && Array.isArray(view?.noteIds)
              ? view.noteIds.map(Number)
              : [],
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      await fetchViews(authToken);
      return res.data?.view || null;
    },
    [fetchViews, token]
  );

  const fetchAllNotesForViewEditor = useCallback(
    async (authToken = token) => {
      if (!authToken) return [];

      const pageSize = 100;
      let offset = 0;
      let hasMore = true;
      const allNotes = [];

      while (hasMore) {
        const res = await axios.put(
          '/notes/list',
          {
            searchTerm: '',
            viewId: null,
            categoryIds: [],
            categoryMatchMode: 'any',
            uncategorizedOnly: false,
            limit: pageSize,
            offset,
          },
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        allNotes.push(...rows);

        hasMore = rows.length === pageSize;

        if (hasMore) {
          offset += pageSize;
        }
      }

      return allNotes;
    },
    [token]
  );


  const deleteCustomView = useCallback(
    async (viewId, authToken = token) => {
      if (!viewId) return null;

      const res = await axios.delete(`/notes/views/${viewId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      setActiveViewId((currentId) =>
        String(currentId) === String(viewId) ? null : currentId
      );

      await fetchViews(authToken);
      return res.data;
    },
    [fetchViews, token]
  );

  const selectCustomView = useCallback((viewId) => {
    const safeViewId =
      viewId === null || viewId === undefined || viewId === ''
        ? null
        : String(viewId);

    setActiveViewId(safeViewId);
    setSelectedCategoryIds([]);
    setUncategorizedOnly(false);
    setCategoryMatchMode('any');
    setListPageNo(0);
    setNotePageNo(0);
    setCurrentIndex(0);
  }, []);

  const clearActiveView = useCallback(() => {
    setActiveViewId(null);
  }, []);

  const createCategory = useCallback(
    async (name, colorKey = 'yellow', authToken = token) => {
      const res = await axios.post(
        '/notes/categories',
        { name, colorKey },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      await fetchCategories(authToken);
      return res.data;
    },
    [fetchCategories, token]
  );

  const renameCategory = useCallback(
    async (categoryId, name, colorKey, authToken = token) => {
      const body = {};
      if (name !== undefined) body.name = name;
      if (colorKey !== undefined) body.colorKey = colorKey;

      const res = await axios.put(
        `/notes/categories/${categoryId}`,
        body,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      await fetchCategories(authToken);
      return res.data;
    },
    [fetchCategories, token]
  );

  const archiveCategory = useCallback(
    async (categoryId, authToken = token) => {
      const res = await axios.delete(`/notes/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      setSelectedCategoryIds((prev) =>
        prev.filter((id) => Number(id) !== Number(categoryId))
      );
      await fetchCategories(authToken);
      return res.data;
    },
    [fetchCategories, token]
  );

  const reorderCategories = useCallback(
    async (categoryIds, authToken = token) => {
      const res = await axios.put(
        '/notes/categories/reorder',
        { categoryIds },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      await fetchCategories(authToken);
      return res.data;
    },
    [fetchCategories, token]
  );

  const fetchNoteById = useCallback(
    async (id, authToken = token) => {
      if (!id) return null;
      const res = await axios.get(`/notes/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return res.data;
    },
    [token]
  );

  const fetchPage = useCallback(
    async (
      page,
      { resetIndex = true, searchTermOverride = searchTerm } = {}
    ) => {
      try {
        const offset = page * itemsPerPage;

        const res = await axios.put(
          '/notes/list',
          {
            searchTerm: searchTermOverride || '',
            viewId: activeViewId,
            categoryIds: activeViewId ? [] : selectedCategoryIds,
            categoryMatchMode,
            uncategorizedOnly: activeViewId ? false : uncategorizedOnly,
            limit: itemsPerPage,
            offset,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = res.data || {};
        let rows = data.data || [];
        let total = data.total || 0;

        if (rows.length === 0 && total === 0 && notes.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, 350));

          const retryRes = await axios.put(
            '/notes/list',
            {
              searchTerm: searchTermOverride || '',
              viewId: activeViewId,
              categoryIds: activeViewId ? [] : selectedCategoryIds,
              categoryMatchMode,
              uncategorizedOnly: activeViewId ? false : uncategorizedOnly,
              limit: itemsPerPage,
              offset,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const retryData = retryRes.data || {};
          rows = retryData.data || [];
          total = retryData.total || 0;

          if (rows.length === 0 && total === 0) {
            setHasLoaded(true);
            return retryData;
          }
        }

        setNotes(rows);
        setTotalNotesFound(total);
        setHasLoaded(true);

        if (!rows.length) {
          setCurrentIndex(0);
          setCurrentNoteId(null);
          setForm({
            ...INITIAL_FORM,
            userId: userId || null,
          });
          setIsEditing(false);
          return data;
        }

        if (resetIndex) {
          setCurrentIndex(0);

          const firstNote = rows[0];
          if (firstNote) {
            setForm(mapNoteFromApi(firstNote));
            setCurrentNoteId(firstNote.id);
          }
        }

        return {
          ...data,
          data: rows,
          total,
        };
      } catch (err) {
        console.error('Failed to fetch notes page:', err);
        throw err;
      }
    },
    [
      activeViewId,
      categoryMatchMode,
      itemsPerPage,
      mapNoteFromApi,
      notes.length,
      searchTerm,
      selectedCategoryIds,
      token,
      uncategorizedOnly,
      userId,
    ]
  );

  const focusNoteAfterSave = useCallback(
    async (savedNoteId) => {
      if (!savedNoteId) return;

      setSearchTerm('');
      setSearchType('');
      setListPageNo(0);
      setNotePageNo(0);
      setNavSource('tab');

      await fetchCategories(token);

      const data = await fetchPage(0, {
        resetIndex: false,
        searchTermOverride: '',
      });

      const nextNotes = data?.data || [];

      const index = nextNotes.findIndex(
        (note) => Number(note.id) === Number(savedNoteId)
      );

      if (index >= 0) {
        const savedNote = nextNotes[index];

        setCurrentIndex(index);
        setCurrentNoteId(savedNote.id);
        setForm(mapNoteFromApi(savedNote));
      } else {
        const savedNote = await fetchNoteById(savedNoteId, token);

        if (savedNote) {
          setNotes((prev) => [
            savedNote,
            ...prev.filter((note) => Number(note.id) !== Number(savedNote.id)),
          ]);

          setCurrentIndex(0);
          setCurrentNoteId(savedNote.id);
          setForm(mapNoteFromApi(savedNote));
        }
      }

      setActiveTab('notes');
    },
    [
      fetchCategories,
      fetchNoteById,
      fetchPage,
      mapNoteFromApi,
      setSearchTerm,
      setSearchType,
      token,
    ]
  );

  const saveNote = useCallback(
    async (payload, authToken = token) => {
      const title = String(payload.title || '');
      if (!title.trim()) {
        throw new Error('Title is required.');
      }
      if (title.length > NOTE_TITLE_MAX_LENGTH) {
        throw new Error(`Title must be ${NOTE_TITLE_MAX_LENGTH} characters or fewer.`);
      }

      const body = {
        title,
        // title_html is HTML. Convert any literal CR/LF left by the browser
        // into real <br> elements before sending to the API.
        titleHtml: String(payload.titleHtml || '')
          .replace(/\r\n?/g, '\n')
          .replace(/\n/g, '<br>'),
        detail: payload.detail || '',
        detailHtml: payload.detailHtml || '',
        dueAt: payload.dueAt || null,
        remindAt: payload.remindAt || null,
        expiresAt: payload.expiresAt || null,
        sendSmsReminder: !!payload.sendSmsReminder,
        displayMode: payload.displayMode || 'normal',
        categoryIds: Array.isArray(payload.categoryIds) ? payload.categoryIds : [],
        primaryCategoryId:
          payload.primaryCategoryId != null && payload.primaryCategoryId !== ''
            ? Number(payload.primaryCategoryId)
            : null,
      };

      if (payload.id) {
        const res = await axios.put(`/notes/${payload.id}`, body, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        return res.data?.updated || res.data;
      }

      const res = await axios.post('/notes', body, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return res.data;
    },
    [token]
  );

  const deleteNote = useCallback(
    async (id, authToken = token) => {
      if (!id) return null;
      const res = await axios.delete(`/notes/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return res.data;
    },
    [token]
  );

  const reorderNotes = useCallback(
    async (noteIds, authToken = token) => {
      const res = await axios.put(
        '/notes/reorder',
        { noteIds },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      return res.data;
    },
    [token]
  );

  const handleTabClick = (tab) => {
    if (tab === 'notes') {
      setNavSource('tab');
      fetchPage(notePageNo, { resetIndex: false });
      setListPageNo(notePageNo);
    }
    setActiveTab(tab);
  };

  useEffect(() => {
    if (!token) return;

    Promise.all([fetchCategories(token), fetchViews(token)]).catch((err) => {
      console.error('Failed to fetch note categories/views:', err);
    });
  }, [fetchCategories, fetchViews, token]);

  useEffect(() => {
    setListPageNo(0);
    setNotePageNo(0);
    setCurrentIndex(0);
  }, [activeViewId, categoryMatchMode, selectedCategoryIds, uncategorizedOnly]);

  useEffect(() => {
    if (navSource === 'list') {
      setNotePageNo(listPageNo);
    }
  }, [navSource, listPageNo]);

  useEffect(() => {
    fetchPage(listPageNo, { resetIndex: false });
  }, [listPageNo, fetchPage]);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') return;

    setIsEditing(false);
    setCurrentIndex(0);
    setListPageClicked(0);
    setActiveTab('list');
    setListPageNo(0);
  }, [searchTerm]);

  useEffect(() => {
    const id = notes[currentIndex]?.id;
    if (!id || isEditing) return;

    (async () => {
      try {
        const note = await fetchNoteById(id, token);
        if (!note) return;
        setCurrentNoteId(note.id);
        setForm(mapNoteFromApi(note));
      } catch (err) {
        console.error('Failed to fetch note by id:', err);
      }
    })();
  }, [currentIndex, isEditing, notes, fetchNoteById, mapNoteFromApi, token]);

  useEffect(() => {
    if (!token) return;

    let controller = null;
    let heartbeatTimer = null;
    let reconnectTimer = null;
    let stopped = false;

    const apiBase =
      process.env.REACT_APP_API_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      'http://localhost:5001';

    const refreshEvents = new Set([
      'notes:created',
      'notes:updated',
      'notes:deleted',
      'notes:reordered',
      'notes:categories-changed',
      'notes:views-changed',
    ]);

    const cleanupConnection = () => {
      if (controller) {
        controller.abort();
        controller = null;
      }

      if (heartbeatTimer) {
        clearTimeout(heartbeatTimer);
        heartbeatTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (stopped) return;

      cleanupConnection();

      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }

      reconnectTimer = setTimeout(() => {
        if (!stopped) {
          connectSse();
        }
      }, 1500);
    };

    const resetHeartbeat = () => {
      if (heartbeatTimer) {
        clearTimeout(heartbeatTimer);
      }

      // Backend sends ping every 25 seconds. 70 seconds allows missed/delayed pings.
      heartbeatTimer = setTimeout(() => {
        console.warn('[Notes SSE] heartbeat timeout, reconnecting...');
        scheduleReconnect();
      }, 70000);
    };

    const connectSse = () => {
      controller = new AbortController();
      resetHeartbeat();

      console.log('[Notes SSE] connecting...');

      fetchEventSource(`${apiBase}/api/events`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
        openWhenHidden: true,

        onopen: async (response) => {
          console.log('[Notes SSE] connected:', response.status);
          resetHeartbeat();
        },

        onmessage: async (event) => {
          resetHeartbeat();

          if (event.event === 'ping' || event.event === 'connected') {
            return;
          }

          console.log('[Notes SSE] received:', event.event, event.data);

          if (!refreshEvents.has(event.event)) return;

          try {
            await fetchCategories(token);

            if (event.event === 'notes:views-changed') {
              await fetchViews(token);
            }

            await fetchPage(listPageNo, { resetIndex: false });
          } catch (err) {
            console.error('[Notes SSE] refresh failed:', err);
          }
        },

        onerror: (err) => {
          console.warn('[Notes SSE] connection error, reconnecting:', err);
          scheduleReconnect();

          // Prevent fetchEventSource from continuing its own retry loop after we abort.
          throw err;
        },
      });
    };

    connectSse();

    return () => {
      stopped = true;
      cleanupConnection();

      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, listPageNo]);

  return (
    <div className="notes-page">
      <div className="form-wrapper">
        <div className="mydata-tabs-container">
          <div className="tab-buttons">
            <button
              className={activeTab === 'list' ? 'tab active' : 'tab'}
              onClick={() => handleTabClick('list')}
            >
              List
            </button>

            <button
              className={activeTab === 'notes' ? 'tab active' : 'tab'}
              onClick={() => handleTabClick('notes')}
            >
              Notes
            </button>
          </div>

          <div className="tab-panel">
            {activeTab === 'list' && (
              <ListNotesWeb
                currentIndex={currentIndex}
                deleteNoteById={deleteNote}
                reorderNotesByIds={reorderNotes}
                notes={notes}
                setNotes={setNotes}
                fetchPage={fetchPage}
                listPageClicked={listPageClicked}
                listPageNo={listPageNo}
                setActiveTab={setActiveTab}
                setCurrentIndex={setCurrentIndex}
                setCurrentNoteId={setCurrentNoteId}
                setForm={setForm}
                setIsEditing={setIsEditing}
                setListPageClicked={setListPageClicked}
                setListPageNo={setListPageNo}
                setNotePageNo={setNotePageNo}
                totalNotesFound={totalNotesFound}
                token={token}
                userId={userId}
                fetchNoteById={fetchNoteById}
                categories={categories}
                selectedCategoryIds={selectedCategoryIds}
                setSelectedCategoryIds={setSelectedCategoryIds}
                categoryMatchMode={categoryMatchMode}
                setCategoryMatchMode={setCategoryMatchMode}
                uncategorizedOnly={uncategorizedOnly}
                setUncategorizedOnly={setUncategorizedOnly}
                createCategory={createCategory}
                renameCategory={renameCategory}
                archiveCategory={archiveCategory}
                reorderCategories={reorderCategories}
                customViews={customViews}
                activeViewId={activeViewId}
                selectCustomView={selectCustomView}
                clearActiveView={clearActiveView}
                createCustomView={createCustomView}
                updateCustomView={updateCustomView}
                deleteCustomView={deleteCustomView}
                fetchAllNotesForViewEditor={fetchAllNotesForViewEditor}
              />
            )}

            {activeTab === 'notes' && (
              <MyNotesWeb
                currentIndex={currentIndex}
                notes={notes}
                notePageNo={notePageNo}
                fetchPage={fetchPage}
                focusNoteAfterSave={focusNoteAfterSave}
                form={form}
                hasLoaded={hasLoaded}
                isEditing={isEditing}
                itemsPerPage={itemsPerPage}
                listPageNo={listPageNo}
                searchTerm={searchTerm}
                setActiveTab={setActiveTab}
                setCurrentIndex={setCurrentIndex}
                setForm={setForm}
                setIsEditing={setIsEditing}
                setListPageNo={setListPageNo}
                setSearchTerm={setSearchTerm}
                setSearchType={setSearchType}
                token={token}
                totalNotesFound={totalNotesFound}
                userId={userId}
                fetchNoteById={fetchNoteById}
                saveNote={saveNote}
                deleteNote={deleteNote}
                categories={categories}
                selectedCategoryIds={selectedCategoryIds}
                uncategorizedOnly={uncategorizedOnly}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
